/** @jsx jsx */

import { jsx } from '@emotion/core';
import { useEffect } from 'react';

import { useLocalStorage } from '@zapier/common-hooks';
import { Currency, useCurrencies } from '@zapier/currency';
import { Alert } from '@zapier/design-system';

import { Border, Colors } from '@zapier/checkout/src/constants';
import { PlanV2 } from '@zapier/checkout/src/types';
import { useApolloInjection } from '@zapier/checkout/src/contexts/ApolloInjection';

import { invoicePreviewQuery_account_invoicePreview as Invoice } from '../hooks/useInvoicePreview/types/invoicePreviewQuery';

import { AmountDue } from './AmountDue';
import { AmountDueOnNextInterval } from './AmountDueOnNextInterval';
import { ChangePlanSummarySkeleton } from './ChangePlanSummarySkeleton';
import { InvoiceLine } from './InvoiceLine';

type Props = {
  invoice: Invoice | null | undefined;
  error: string | null | undefined;
  isLoading: boolean;
  plan: PlanV2;
};

const breakdownCss = {
  borderBottom: `${Border.size.default} solid ${Border.color.default}`,
  padding: 10,
};

const sectionCss = {
  backgroundColor: Colors.white,
  border: `${Border.size.default} solid ${Border.color.default}`,
  borderRadius: Border.radius.default,
  lineHeight: '1.6em',
  overflow: 'hidden',
};

export const ChangePlanSummary = ({
  error,
  invoice,
  isLoading,
  plan,
}: Props) => {
  const { useQuery } = useApolloInjection();
  const { currencies } = useCurrencies({ useQuery });
  const [invoicedAtDate, setInvoicedAtDate] = useLocalStorage('invoicedAtDate');

  useEffect(() => {
    if (!isLoading && invoice) {
      setInvoicedAtDate(invoice?.invoicedAt);
    }
  }, [invoice, invoicedAtDate, isLoading, setInvoicedAtDate]);

  if (isLoading || !invoice) {
    return <ChangePlanSummarySkeleton />;
  }

  if (error || !plan) {
    return <Alert type="error">{error}</Alert>;
  }

  const tiers = plan.tiers;
  const freeTier = tiers.find(tier => tier.pricePerTask === 0);
  const planNumFreeMeteredTasks = freeTier ? freeTier.numTasks : 0;

  return (
    <section css={sectionCss}>
      {invoice.items.length > 0 && (
        <div css={breakdownCss}>
          {invoice.items.map((line: any) => (
            <InvoiceLine key={line.id} line={line} />
          ))}
        </div>
      )}
      <AmountDue
        amountDue={invoice.amountDue}
        currency={currencies?.find(
          (c: Currency) => c.name === invoice.currency
        )}
      />
      {plan.amount > 0 && (
        <AmountDueOnNextInterval
          isPlanMetered={tiers.length > 0}
          meteredTiers={tiers}
          nextPaymentDate={invoice.periodEnd}
          planBillingAmount={plan.amount}
          planInterval={plan.interval}
          planNumFreeMeteredTasks={planNumFreeMeteredTasks}
        />
      )}
    </section>
  );
};
